import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useWallet, useCreditPlans } from '@/hooks/useWallet';

export type SourceExportReason = 'admin' | 'paid' | 'free' | 'anonymous';

/**
 * Who may export the raw LaTeX source of a Writer version.
 *
 * Allowed for admins and for users whose current wallet plan is a *paid*
 * plan — derived from `credit_plans.price_eur_monthly > 0` rather than a
 * string comparison against 'free', so future plan codes stay correct.
 *
 * We deliberately do NOT gate on `credit_subscriptions.status`: `trialing`
 * and `past_due` are legitimate paid states, and the Paddle webhook already
 * downgrades the wallet to the free plan on cancel/pause.
 *
 * This is the UI mirror of the `writer_export_source` RPC, which enforces the
 * same rule server-side.
 */
export function useSourceExportEntitlement(): {
  allowed: boolean;
  reason: SourceExportReason;
  loading: boolean;
} {
  const { user } = useAuth();
  const wallet = useWallet();
  const plans = useCreditPlans();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setIsAdmin(false);
      setAdminLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setAdminLoading(true);
    (async () => {
      try {
        // Writer pro users share the admin export right (the server agrees).
        const [adminRes, proRes] = await Promise.all([
          supabase.rpc('has_role', { _role: 'admin' }),
          supabase.rpc('has_role', { _role: 'writer_pro' as never }),
        ]);
        if (cancelled) return;
        setIsAdmin(
          (!adminRes.error && adminRes.data === true) || (!proRes.error && proRes.data === true),
        );
      } catch {
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setAdminLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) return { allowed: false, reason: 'anonymous', loading: false };

  const loading = adminLoading || wallet.isLoading || plans.isLoading;
  if (isAdmin) return { allowed: true, reason: 'admin', loading: false };

  const planCode = wallet.data?.planCode;
  const plan = (plans.data ?? []).find((p) => p.planCode === planCode);
  const paid = !!plan && plan.priceEurMonthly > 0;

  if (loading) return { allowed: false, reason: 'free', loading: true };
  return { allowed: paid, reason: paid ? 'paid' : 'free', loading: false };
}
